import { useEffect, useRef } from 'react';
import { getAccountFeatureFlags } from '../api/feature-flag.api';

const POLL_INTERVAL_MS = 60 * 1000;
const REFRESH_BANNER_ID = 'account-feature-flag-refresh-banner';
const TEST_BANNER_ID = 'account-feature-flag-test-banner';
const TEST_FLAG_KEY = 'account_test_banner';

const normalizeValue = (flag) => {
  const raw = flag.resolved ?? flag.value ?? false;
  if (typeof raw === 'boolean') return raw;
  if (typeof raw === 'string') return raw.toLowerCase() === 'true';
  return Boolean(raw);
};

const normalizeFlags = (flags) => {
  const map = {};
  (flags || []).forEach((flag) => {
    if (!flag || !flag.key) return;
    map[flag.key] = normalizeValue(flag);
  });
  return map;
};

const showRefreshBanner = () => {
  if (document.getElementById(REFRESH_BANNER_ID)) return;

  const style = document.createElement('style');
  style.id = `${REFRESH_BANNER_ID}-style`;
  style.textContent = `
    @keyframes accountFlagBannerSlideIn {
      from { transform: translateY(-100%); opacity: 0; }
      to { transform: translateY(0); opacity: 1; }
    }
  `;
  document.head.appendChild(style);

  const banner = document.createElement('div');
  banner.id = REFRESH_BANNER_ID;
  banner.style.cssText = `
    position: fixed; top: 0; left: 0; right: 0; z-index: 999999;
    background: #014671; color: white; padding: 12px 20px;
    display: flex; align-items: center; justify-content: center; gap: 16px;
    font-family: 'Roboto', sans-serif; font-size: 14px;
    box-shadow: 0 2px 8px rgba(0,0,0,0.25);
    animation: accountFlagBannerSlideIn 0.3s ease-out;
  `;
  banner.innerHTML = `
    <span>Account feature flags have been updated.</span>
    <button id="${REFRESH_BANNER_ID}-refresh" style="
      background: #40B9C3; color: white; border: none; padding: 7px 18px;
      border-radius: 4px; cursor: pointer; font-weight: 600; font-size: 13px;
      flex-shrink: 0;
    ">Refresh</button>
    <button id="${REFRESH_BANNER_ID}-dismiss" style="
      background: transparent; color: #ccc; border: none; cursor: pointer;
      font-size: 18px; padding: 0 4px; margin-left: 4px; flex-shrink: 0;
    " aria-label="Dismiss">&#x2715;</button>
  `;

  document.body.prepend(banner);

  document.getElementById(`${REFRESH_BANNER_ID}-refresh`).addEventListener('click', () => {
    window.location.reload();
  });
  document.getElementById(`${REFRESH_BANNER_ID}-dismiss`).addEventListener('click', () => {
    banner.remove();
    const s = document.getElementById(`${REFRESH_BANNER_ID}-style`);
    if (s) s.remove();
  });
};

const removeTestBanner = () => {
  const banner = document.getElementById(TEST_BANNER_ID);
  if (banner) banner.remove();
  const style = document.getElementById(`${TEST_BANNER_ID}-style`);
  if (style) style.remove();
};

const showTestBanner = () => {
  if (document.getElementById(TEST_BANNER_ID)) return;

  const style = document.createElement('style');
  style.id = `${TEST_BANNER_ID}-style`;
  style.textContent = `
    @keyframes accountTestBannerSlideIn {
      from { transform: translateX(-100%); opacity: 0; }
      to { transform: translateX(0); opacity: 1; }
    }
  `;
  document.head.appendChild(style);

  const banner = document.createElement('div');
  banner.id = TEST_BANNER_ID;
  banner.style.cssText = `
    position: fixed; bottom: 24px; left: 24px; z-index: 999998;
    background: #ffffff; color: #1a1a1a; padding: 12px 16px;
    border-radius: 12px; box-shadow: 0 4px 24px rgba(0,0,0,0.18);
    font-family: 'Roboto', sans-serif; font-size: 13px;
    display: flex; align-items: center; gap: 10px;
    min-width: 200px; max-width: 300px;
    border-left: 4px solid #40B9C3;
    animation: accountTestBannerSlideIn 0.3s ease-out;
  `;
  banner.innerHTML = `
    <span style="font-size: 18px;">🧪</span>
    <div style="display: flex; flex-direction: column; gap: 2px;">
      <span style="font-weight: 700; color: #014671;">Account Test Banner Active</span>
      <span style="font-size: 12px; color: #555;">This banner is shown because the <code>${TEST_FLAG_KEY}</code> flag is enabled for this account.</span>
    </div>
    <button id="${TEST_BANNER_ID}-dismiss" style="
      background: transparent; color: #999; border: none; cursor: pointer;
      font-size: 16px; padding: 0 2px; margin-left: auto; flex-shrink: 0;
    " aria-label="Dismiss">&#x2715;</button>
  `;

  document.body.appendChild(banner);

  document.getElementById(`${TEST_BANNER_ID}-dismiss`).addEventListener('click', () => {
    removeTestBanner();
  });
};

const useAccountFeatureFlagPolling = (isLoggedIn, accountId) => {
  const flagsAtPageLoad = useRef(null);
  const timeoutRef = useRef(null);
  const remainingMsRef = useRef(POLL_INTERVAL_MS);
  const lastVisibleAtRef = useRef(Date.now());

  const schedulePoll = (delayMs) => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    lastVisibleAtRef.current = Date.now();
    remainingMsRef.current = delayMs;
    timeoutRef.current = setTimeout(() => {
      poll();
      schedulePoll(POLL_INTERVAL_MS);
    }, delayMs);
  };

  const poll = async () => {
    if (!accountId) return;

    try {
      const flags = await getAccountFeatureFlags(accountId);
      const current = normalizeFlags(flags);

      // Account-specific test banner controlled directly by account flags
      if (current[TEST_FLAG_KEY]) {
        showTestBanner();
      } else {
        removeTestBanner();
      }

      // First successful fetch establishes the snapshot for this account
      if (flagsAtPageLoad.current === null) {
        flagsAtPageLoad.current = current;
        return;
      }

      const snapshot = flagsAtPageLoad.current;
      const allKeys = new Set([...Object.keys(snapshot), ...Object.keys(current)]);
      let hasChanged = false;

      for (const key of allKeys) {
        const inSnapshot = key in snapshot;
        const inCurrent = key in current;
        if (!inSnapshot || !inCurrent || snapshot[key] !== current[key]) {
          hasChanged = true;
          break;
        }
      }

      if (hasChanged) {
        showRefreshBanner();
        // Update the baseline so the banner does not reappear on every poll
        flagsAtPageLoad.current = current;
      }
    } catch {
      // Fail silently so polling never breaks the app
    }
  };

  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.hidden) {
        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current);
          timeoutRef.current = null;
          const elapsed = Date.now() - lastVisibleAtRef.current;
          remainingMsRef.current = Math.max(0, remainingMsRef.current - elapsed);
        }
      } else {
        schedulePoll(remainingMsRef.current);
      }
    };

    // Reset any account-specific DOM elements when logged out
    if (!isLoggedIn || !accountId) {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
      remainingMsRef.current = POLL_INTERVAL_MS;
      flagsAtPageLoad.current = null;
      removeTestBanner();
      return;
    }

    if (!document.hidden) {
      schedulePoll(POLL_INTERVAL_MS);
    }

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
      remainingMsRef.current = POLL_INTERVAL_MS;
      flagsAtPageLoad.current = null;
      removeTestBanner();
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [isLoggedIn, accountId]);
};

export default useAccountFeatureFlagPolling;
